// src/components/Layout/CanvasArea/CanvasArea.tsx
import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Maximize2, Maximize } from 'lucide-react';
import { IsometricCanvas } from '@components/Canvas/IsometricCanvas';
import { useInitialDataManager } from 'src/hooks/useInitialDataManager';
import { useUiStateStore } from '@stores/uiStateStore';
import { INITIAL_DATA } from 'src/config';
import { useModelStore } from '@/stores/modelStore';
import { useDiagramUtils } from '@/hooks/useDiagramUtils';
import { EditableTitle } from './EditableTitle';
import { PresentationMode } from './PresentationMode';

const EDITOR_SIDEBAR_WIDTH = 320;

interface CanvasAreaProps {
  isLeftSidebarOpen?: boolean;
  isRightSidebarOpen?: boolean;
}

export const CanvasArea: React.FC<CanvasAreaProps> = ({
  isLeftSidebarOpen = false,
  isRightSidebarOpen = false
}): React.ReactElement => {
  const initialDataManager = useInitialDataManager();
  const hasInitialized = useRef(false);
  const [loadAttempted, setLoadAttempted] = useState(false);
  const [isPresentationMode, setIsPresentationMode] = useState(false);
  const rendererEl = useUiStateStore((state) => state.rendererEl);
  const showGrid = useUiStateStore((state) => state.showGrid);
  const { fitToView } = useDiagramUtils();

  // Get title from modelStore
  const title = useModelStore((state) => state.title);
  const setTitle = useModelStore((state) => state.actions.setTitle);

  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
  };

  const handleEnterPresentationMode = () => {
    // Enter presentation mode - fullscreen handled by PresentationMode component
    setIsPresentationMode(true);
  };

  const handleFitToView = () => {
    fitToView({
      left: isLeftSidebarOpen ? EDITOR_SIDEBAR_WIDTH : 0,
      right: isRightSidebarOpen ? EDITOR_SIDEBAR_WIDTH : 0
    });
  };

  // Try to initialize immediately, without waiting for rendererEl
  useEffect(() => {
    if (!hasInitialized.current && !loadAttempted) {
      hasInitialized.current = true;
      setLoadAttempted(true);

      // Load WITHOUT fitToView first - rendererEl might not be ready yet
      initialDataManager.load({
        ...INITIAL_DATA,
        fitToView: false // Changed to false so it doesn't wait for rendererEl
      });
    }
  }, [initialDataManager, loadAttempted]);

  // Once rendererEl is available and data is ready, fit to view
  useEffect(() => {
    if (initialDataManager.isReady && rendererEl) {
      // Optionally fit to view after everything is loaded
      // This can be done later via a button or automatically
    }
  }, [initialDataManager.isReady, rendererEl]);

  // Show loading state
  if (!initialDataManager.isReady) {
    return (
      <div className="flex flex-1 items-center justify-center bg-white dark:bg-slate-950">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 h-9 w-9 animate-spin text-blue-500" aria-hidden="true" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Loading canvas...
          </p>
          <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
            {!loadAttempted ? 'Initializing...' : 'Preparing diagram...'}
          </p>
        </div>
      </div>
    );
  }

  // Just the canvas for presentation mode (grid visible, no hover interactions)
  const presentationCanvas = (
    <div className="w-full h-full relative overflow-hidden bg-white dark:bg-slate-950">
      <IsometricCanvas
        showGrid={showGrid}
        backgroundColor="transparent"
        enableInteractions={false}
      />
    </div>
  );

  const normalView = (
    <div className="flex flex-1 flex-col overflow-hidden bg-white dark:bg-slate-950">
      {/* Canvas Header*/}
      <div
        className="flex h-10 items-center justify-between border-b border-gray-200 transition-all duration-300 bg-gray-50 px-4 dark:border-slate-700 dark:bg-slate-900"
        style={{
          paddingLeft: isLeftSidebarOpen ? '336px' : '16px', // 320px sidebar + 16px padding
          paddingRight: isRightSidebarOpen ? '336px' : '16px' // 320px sidebar + 16px padding
        }}
      >
        <EditableTitle
          value={title}
          onChange={handleTitleChange}
          placeholder="Untitled Diagram"
        />
        {/* <span className="text-xs text-gray-500 dark:text-gray-400">
          Untitled Diagram
        </span> */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleFitToView}
            className="rounded p-1 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors"
            title="Fit to Screen"
            aria-label="Fit diagram to screen"
          >
            <Maximize className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          </button>
          <button
            type="button"
            onClick={handleEnterPresentationMode}
            className="rounded p-1 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors"
            title="Enter Presentation Mode (Fullscreen)"
            aria-label="Enter presentation mode"
          >
            <Maximize2 className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          </button>
        </div>
      </div>

      {/* Canvas Content - IsometricCanvas from Phase 2 */}
      <div className="relative flex-1 overflow-hidden" id="canvas-container">
        <IsometricCanvas showGrid={showGrid} backgroundColor="transparent" />
      </div>

      {/* Canvas Footer - Optional zoom/position info */}
      {/* <div className="flex h-8 items-center justify-between border-t border-gray-200 bg-gray-50 px-4 dark:border-slate-700 dark:bg-slate-900">
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Zoom: 100%
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Position: 0, 0
        </span>
      </div> */}
    </div>
  );

  return isPresentationMode ? (
    <PresentationMode onExit={() => setIsPresentationMode(false)}>
      {presentationCanvas}
    </PresentationMode>
  ) : (
    normalView
  );
};
